<template lang="pug">
  .mr-newsletter-subscription
    h2.title.f-secondary.brand-color-1.xs-f-xxlarge.max-at-tweak(for="newsletter-signup") {{title}}
    label.title-text.light.brand-color-1.xs-f-xsmall.max-at-tweak(v-if="!isSubscribed && !justSubscribed" for="newsletter-signup") {{subtitle}}

    p.confirmation-text(v-if="isSubscribed || justSubscribed") You're on the list!

    form(v-if="!isSubscribed && !justSubscribed" name="newsletterForm" ref="newsletterForm" @submit.prevent="onSubmit")
      input#newsletter-signup(name="emailInput" @keydown="verifyEmail" type='email' placeholder='Enter your email' v-model="email" @input="$v.email.$touch()" required :class="{disabled: submitting}" :disabled="submitting")
      
      .mail-note.xs-f-xsmall.sm-f-xxsmall.max-at-tweak.sm-hide.md-hide.lg-hide(v-if="mailCheckedEmail" @click="setEmail")
        | Did you mean&nbsp;
        .inner-email {{mailCheckedEmail}}
        |?
      
      p.error-message.feedback-color-1.xs-f-xsmall.sm-f-xxsmall.max-at-tweak.sm-hide.md-hide.lg-hide(v-if="errorMessage") {{errorMessage}}
      
      button.mr-btn.btn-secondary.xs-f-xsmall.lg-f-xxsmall.max-at-tweak(v-if='!isSubscribed' :class="{disabled: submitting}" type="submit" aria-label="join" :disabled="submitting") 
        MrSpinner(v-if="submitting")
        span(v-else) {{buttonText}}

      .mail-note.xs-f-xsmall.sm-f-xxsmall.max-at-tweak.xs-hide(v-if="mailCheckedEmail" @click="setEmail")
        | Did you mean&nbsp;
        .inner-email {{mailCheckedEmail}}
        |?
      
      p.error-message.feedback-color-1.xs-f-xsmall.sm-f-xxsmall.max-at-tweak.xs-hide(v-if="errorMessage") {{errorMessage}}

      .additional-note.xs-f-xxsmall.max-at-tweak(v-if="!showCaptcha") {{additionalNote}}

    .captcha(v-if="showCaptcha")
      Captcha(@captcha-response="setCaptchaResponse")
</template>

<script>
import { newsletterSignUp, getSignInData } from '@services/vueCustomerSvc';
import Captcha from '@components/captcha';
import MrSpinner from '@components/MrSpinner';
const { required, email } = require('vuelidate/lib/validators');

let Mailcheck;

export default {
  name: 'MrNewsletterSubscription',

  components: {
    Captcha,
    MrSpinner,
  },

  props: {
    buttonText: {
      type: String,
      default: 'JOIN',
    },

    subtitle: {
      type: String,
      default: 'Share your email to get FREE SHIPPING on your first order, plus the inside scoop on exclusive promotions, hair tips, and events near you.',
    },

    unsubscribedTitle: {
      type: String,
      default: "Let's stay in touch",
    },

    subscribedTitle: {
      type: String,
      default: "Thanks for subscribing!",
    },

    additionalNote: {
      type: String,
      default: 'Free standard shipping on first purchase. Valid only for new customers. Unsubscribe anytime.',
    },

    source: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    isSubscribed: false,
    justSubscribed: false,
    mailCheckedEmail: false,
    showCaptcha: false,
    email: '',
    captchaResponse: false,
    errorMessage: undefined,
    submitting: false,
  }),

  validations: {
    email: {
      required,
      email,
    },
  },

  computed: {
    title() {
      return this.justSubscribed ? this.subscribedTitle : this.unsubscribedTitle;
    },
  },

  async mounted() {
    // Client-only imports for SSR
    const {default: sentry} = await import ('../../addSentry.js');
    Mailcheck = require('mailcheck');

    getSignInData()
      .then(results => {
        if (results.data.email) {
          this.isSubscribed = true;
        }
      })
      .catch(err => {
        // send to sentry
        sentry.withScope(scope => {
          scope.setTag('vueComponent', 'mrNewsletterSubscription');
          sentry.captureException(err);
        });
      });
  },
  
  methods: {    
    setCaptchaResponse(response) {
      this.captchaResponse = response;
      this.showCaptcha = false;
      this.errorMessage = undefined;
    },

    onSubmit() {
      if (this.$v.$invalid || this.submitting) {
        return;
      }

      this.submitting = true;

      newsletterSignUp({
        email: this.email,
        source: this.source,
        captchaResponse: this.captchaResponse,
      })
        .then(() => {
          this.justSubscribed = true;
          this.showCaptcha = false;
          this.errorMessage = undefined;
          this.submitting = false;
        })
        .catch(err => {
          const error = err.response;
          
          this.submitting = false;

          if (error && error.data && error.data.code == 'CAPTCHA') {
            this.showCaptcha = true;
          }

          if (error && error.data && error.data.errors) {
            this.errorMessage = error.data.errors.email;
          } else if (error && error.data && error.data.code == 'MISSING_PASSWORD') {
            this.openEmailSentModal();
          } else {
            this.errorMessage = error.data.message;
          }
        });
    },

    openEmailSentModal() {
      let payload = {
        component: './MrSignIn',
        theme: 'signin',
        props: {
          userEmail: this.email,
          startView: "emailSent",
        },
      };

      this.showModal(payload);
    },

    verifyEmail() {
      if (!Mailcheck) {
        return;
      }

      Mailcheck.run({
        email: this.email,
        suggested: suggestion => {
          if (suggestion.full == email) {
            return (this.mailCheckedEmail = false);
          }
          this.mailCheckedEmail = suggestion.full;
        },
        empty: () => {
          this.mailCheckedEmail = false;
        },
      });
    },

    setEmail() {
      this.email = this.mailCheckedEmail;
      this.mailCheckedEmail = false;
    },
  },
};
</script>

<style lang="stylus">
.mr-newsletter-subscription
  .title
    margin-bottom 0.18em

  .title-text
  .error-message
  .mail-note
    @media mq-mobile
      text-align left

  .title-text
    display block
    padding-bottom 1.8em
  
  .mail-note
    margin-top 6px

    .inner-email
      text-decoration underline
      cursor pointer
      display inline-block
      
  input
  button
    display inline-block
    vertical-align top
    height 44px
    
  input
    width 20em
    
  button
    width 8em
    padding 0em
    display inline-flex
    justify-content center
    align-items center


  .additional-note
    margin-top 6px

  .error-message
    margin-top 6px
  
  @media mq-mobile
    input
    button
      width 100%
      
    button
      margin-top 0.5em

    .additional-note
      text-align left

  @media mq-tablet-plus
    input
      display inline-block
      vertical-align top
      border-right none
      border-top-right-radius 0
      border-bottom-right-radius 0
      width 24em

  @media mq-tablet
    input
      width 22em
</style>
